import * as React from 'react';
import { parseDocument } from 'htmlparser2';
import { textContent } from 'domutils';
import type { AccordionSectionContent } from '../../../types';

interface StructuredDataProps {
  sections: AccordionSectionContent[];
  headline?: string;
}

/**
 * Strip HTML tags and decode entities from text for structured data
 * Uses htmlparser2 for consistent SSR/CSR behavior to avoid hydration errors
 * @param html - HTML string to clean
 * @returns Plain text without HTML tags or entities
 */
const stripHtml = (html: string): string => {
  // Parse HTML using htmlparser2
  const document = parseDocument(html);

  // Extract text content
  const text = textContent(document);

  // Normalize whitespace
  return text.replace(/\s+/g, ' ').trim();
};

/**
 * StructuredData component renders JSON-LD FAQPage schema for search engines
 * @see https://schema.org/FAQPage
 * @see https://developers.google.com/search/docs/appearance/structured-data/faqpage
 */
export const StructuredData: React.FC<StructuredDataProps> = ({ sections, headline }) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    ...(headline && { name: stripHtml(headline) }),
    mainEntity: sections.map((section) => ({
      '@type': 'Question',
      name: stripHtml(section.label),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtml(section.body),
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};
